import { motion } from 'framer-motion';

// Shared container style for consistency
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Instant Data Aggregation (Smart Search)
export const DataAggregationVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] bg-white p-4 rounded-lg mx-auto flex flex-col gap-5 relative z-10">

                {/* Search Bar Simulation */}
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="h-10 bg-white rounded-lg shadow-sm border border-blue-100 flex items-center px-3 gap-3 relative overflow-hidden"
                >
                    <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="text-xs font-medium text-gray-700">Market Size: <span className="text-gray-400">GenAI Growth 2025</span></span>

                    {/* Scanning Progress Bar */}
                    <motion.div
                        initial={{ width: "0%" }}
                        whileInView={{ width: "100%" }}
                        transition={{ duration: 2, ease: "easeInOut" }}
                        className="h-[2px] absolute bottom-0 left-0 bg-blue-500"
                    />
                </motion.div>

                {/* Status Text */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="flex justify-between items-center text-[10px] text-gray-400 font-medium px-1 uppercase tracking-wider"
                >
                    <span>Scanning 1.2M+ Reports</span>
                    <span className="text-blue-600">4 Matches Found</span>
                </motion.div>

                {/* Sources Grid */}
                <div className="grid grid-cols-2 gap-3">
                    {[
                        { name: "Bloomberg", type: "Market Report" },
                        { name: "TechCrunch", type: "News Article" },
                        { name: "Statista", type: "Data Set" },
                        { name: "Reuters", type: "Global News" }
                    ].map((source, i) => (
                        <motion.div
                            key={i}
                            initial={{ y: 10, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 + (i * 0.1), duration: 0.4 }}
                            className="bg-white p-3 rounded-lg border border-blue-50 shadow-sm flex items-center gap-3 relative overflow-hidden group"
                        >
                            <div className="w-8 h-8 rounded-md bg-blue-50/50 flex items-center justify-center shrink-0">
                                <span className="text-[10px] font-bold text-blue-600">{source.name[0]}</span>
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="text-[11px] font-semibold text-gray-800 truncate">{source.name}</span>
                                <span className="text-[9px] text-gray-400 truncate">{source.type}</span>
                            </div>

                            {/* Verified Badge tick */}
                            <motion.div
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                transition={{ delay: 1 + (i * 0.1), type: "spring" }}
                                className="absolute top-2 right-2 w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center"
                            >
                                <svg className="w-2 h-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </motion.div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </VisualContainer>
    );
};

// Component 2: Auto-Citation & Verification (Document Check)
export const CitationVerificationVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] bg-white rounded-lg shadow-md border border-gray-100 p-6 relative">

                {/* Document Content */}
                <div className="space-y-4 font-jakarta">
                    <div className="flex flex-col gap-2">
                        {/* Abstract Title */}
                        <div className="w-24 h-3 bg-gray-200 rounded-sm mb-1" />

                        {/* Text Lines */}
                        <div className="w-full h-2 bg-gray-100 rounded-full" />
                        <div className="w-11/12 h-2 bg-gray-100 rounded-full" />
                    </div>

                    {/* Highlighted Verified Sentence */}
                    <div className="relative group">
                        <motion.div
                            initial={{ width: "0%" }}
                            whileInView={{ width: "100%" }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="absolute inset-0 bg-blue-100/50 rounded-sm -mx-1 px-1 h-full"
                        />
                        <p className="text-[11px] leading-relaxed text-gray-800 font-medium relative z-10">
                            "The global AI market size is projected to expand at a CAGR of 37.3% from 2023 to 2030."
                        </p>

                        {/* Source Card Popover */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0, x: 20 }}
                            whileInView={{ scale: 1, opacity: 1, x: 0 }}
                            transition={{ delay: 1.2, type: "spring" }}
                            className="absolute right-0 sm:-right-32 top-full sm:top-1/2 mt-2 sm:mt-0 sm:-translate-y-1/2 bg-white p-2 sm:p-3 rounded-lg shadow-xl border border-blue-100 w-32 sm:w-40 z-20"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[9px] font-bold text-gray-400 uppercase">Source</span>
                                <div className="flex items-center gap-1 bg-green-50 px-1.5 py-0.5 rounded-full border border-green-100">
                                    <svg className="w-2 h-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-[8px] font-bold text-green-700 uppercase">Verified</span>
                                </div>
                            </div>

                            <p className="text-[10px] font-bold text-gray-900 leading-tight mb-1">
                                Grand View Research
                            </p>
                            <p className="text-[9px] text-gray-500">
                                Artificial Intelligence Market Report, 2023-2030
                            </p>

                            <div className="mt-2 flex items-center gap-1.5 pt-2 border-t border-gray-50">
                                <div className="w-3 h-3 rounded-full bg-blue-100 flex items-center justify-center text-[6px] text-blue-600 font-bold">L</div>
                                <span className="text-[8px] text-blue-600 font-medium">Link to Report</span>
                            </div>
                        </motion.div>

                        {/* Connecting Line */}
                        <motion.div
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            transition={{ delay: 1, duration: 0.5 }}
                            className="absolute right-0 top-1/2 w-16 h-[2px] pointer-events-none"
                        >
                            <svg width="60" height="2" className="overflow-visible absolute right-[-5px] top-0">
                                <line x1="0" y1="0" x2="60" y2="0" stroke="#3B82F6" strokeWidth="1" strokeDasharray="3 3" />
                                <circle cx="60" cy="0" r="2" fill="#3B82F6" />
                            </svg>
                        </motion.div>
                    </div>

                    <div className="flex flex-col gap-2 pt-1">
                        <div className="w-full h-2 bg-gray-100 rounded-full" />
                        <div className="w-4/5 h-2 bg-gray-100 rounded-full" />
                    </div>
                </div>
            </div>
        </VisualContainer>
    );
};
